import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, Pencil, RefreshCw, Video, X } from "lucide-react";
import { toast } from "sonner";
import AudienceSelector, { audienceLabel } from "@/components/admin/AudienceSelector";
import { api, formatApiError } from "@/lib/api";

const empty = {
  title: "",
  description: "",
  audience: "all",
  target_levels: [],
  client_id: "",
  published: true,
};

export default function VideoLibraryPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [videos, people] = await Promise.all([api.get("/video-library"), api.get("/clients")]);
      setItems(videos.data || []);
      setClients(people.data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };
  useEffect(() => { load(); }, []);

  const visibleCount = useMemo(
    () => items.filter((item) => item.published && item.in_playlist !== false).length,
    [items],
  );

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/video-library/sync");
      await load();
      toast.success(`${data.synced} video YouTube’dan güncellendi`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSyncing(false);
    }
  };

  const edit = (item) => {
    setEditing(item);
    setForm({ ...empty, ...item, client_id: item.client_id || "" });
    globalThis.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const save = async (event) => {
    event.preventDefault();
    if (!editing) return;
    if (form.audience === "all" && form.published && !window.confirm("Bu video yeni gelen kullanıcılar dahil tüm portal kullanıcılarına açılacak. Devam edilsin mi?")) return;
    setSaving(true);
    try {
      await api.put(`/video-library/${editing.id}`, {
        title: form.title,
        description: form.description || "",
        audience: form.audience,
        target_levels: form.target_levels || [],
        client_id: form.client_id || null,
        published: Boolean(form.published),
      });
      setEditing(null);
      setForm(empty);
      await load();
      toast.success("Video yayını güncellendi");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[1200px] p-3 pb-20 sm:p-5 lg:p-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center bg-pleiades-blue text-white"><Video size={18} /></div>
          <div>
            <h1 className="text-xl font-semibold">Video Kütüphanesi</h1>
            <p className="text-[12px] text-pleiades-slate">YouTube röportajlarını senkronize edin ve görünürlüğünü yönetin.</p>
          </div>
        </div>
        <button type="button" onClick={sync} disabled={syncing} className="flex min-h-11 items-center justify-center gap-2 border border-pleiades-blue bg-white px-4 text-[12px] font-semibold text-pleiades-blue disabled:opacity-50">
          <RefreshCw size={15} className={syncing ? "animate-spin" : ""} />
          {syncing ? "YouTube kontrol ediliyor…" : "YouTube’dan senkronize et"}
        </button>
      </header>

      <div className="mb-4 grid grid-cols-2 border border-pleiades-border bg-white text-center">
        <div className="border-r border-pleiades-border p-3"><strong className="block text-lg">{items.length}</strong><span className="text-[10px] text-pleiades-slate">Toplam video</span></div>
        <div className="p-3"><strong className="block text-lg">{visibleCount}</strong><span className="text-[10px] text-pleiades-slate">Yayında</span></div>
      </div>

      {editing && (
        <form onSubmit={save} className="mb-4 border border-pleiades-border bg-white p-4 sm:p-5">
          <div className="mb-4 flex items-center justify-between gap-3"><strong className="text-[13px]">Videoyu düzenle</strong><button type="button" onClick={() => setEditing(null)} className="grid h-9 w-9 place-items-center" aria-label="Düzenlemeyi kapat"><X size={16} /></button></div>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Başlık"><input className="input" required maxLength={180} value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></Field>
            <label className="flex min-h-11 items-center gap-2 text-[12px]"><input type="checkbox" checked={form.published} onChange={(event) => setForm({ ...form, published: event.target.checked })} /> Portalda yayınla</label>
          </div>
          <div className="mt-3"><Field label="Açıklama"><textarea className="input min-h-28 py-3" maxLength={5000} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></Field></div>
          <div className="mt-3"><AudienceSelector value={form} onChange={setForm} clients={clients} /></div>
          <button disabled={saving} className="mt-4 min-h-11 w-full bg-pleiades-blue px-5 text-[12px] font-semibold text-white disabled:opacity-50 sm:w-auto">{saving ? "Kaydediliyor…" : "Değişiklikleri kaydet"}</button>
        </form>
      )}

      <section className="border border-pleiades-border bg-white" aria-label="YouTube videoları">
        <div className="border-b border-pleiades-border px-4 py-3 text-[13px] font-semibold">Röportajlar ve Söyleşiler · {items.length}</div>
        {items.length ? items.map((item) => (
          <article key={item.id} className="flex items-start gap-3 border-b border-pleiades-border p-3 last:border-0 sm:p-4">
            <img src={item.thumbnail_url} alt="" loading="lazy" className="aspect-video w-28 shrink-0 object-cover sm:w-36" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-pleiades-blue">{item.published && item.in_playlist !== false ? <Eye size={13} /> : <EyeOff size={13} />}{item.published ? "Yayında" : "Pasif"}</div>
              <h2 className="mt-1 line-clamp-2 text-[13px] font-semibold leading-snug">{item.title}</h2>
              <p className="mt-1 text-[10px] text-pleiades-slate">{item.in_playlist === false ? "YouTube listesinden kaldırılmış" : audienceLabel(item, clients)}</p>
            </div>
            <button type="button" onClick={() => edit(item)} aria-label={`${item.title} videosunu düzenle`} className="grid h-10 w-10 shrink-0 place-items-center text-pleiades-slate hover:text-pleiades-blue"><Pencil size={15} /></button>
          </article>
        )) : <div className="p-10 text-center text-[12px] text-pleiades-slate">Videoları almak için “YouTube’dan senkronize et” düğmesine basın.</div>}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="label">{label}</span>{children}</label>;
}
